import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Truck, MapPin, GripVertical, Package, AlertTriangle, ArrowRight, Eye, Printer } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ordersApi, type TruckLoadView, type TruckLoadStop } from '@/services/api';
import { optimizeRoute, computeRouteDistance } from '@/services/routing';

// Origen de la ruta: almacén central en Puente Genil (Córdoba).
const PUENTE_GENIL = { latitude: 37.38704, longitude: -4.76690 };

interface TruckLoadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  routeId: string;
  routeName: string;
  date: string;
}

// Paleta determinista por stopKey
const STOP_COLORS = [
  'bg-sky-200 text-sky-900 border-sky-300',
  'bg-amber-200 text-amber-900 border-amber-300',
  'bg-emerald-200 text-emerald-900 border-emerald-300',
  'bg-rose-200 text-rose-900 border-rose-300',
  'bg-violet-200 text-violet-900 border-violet-300',
  'bg-lime-200 text-lime-900 border-lime-300',
  'bg-orange-200 text-orange-900 border-orange-300',
  'bg-cyan-200 text-cyan-900 border-cyan-300',
];

// Mismos tonos en HEX para el canvas 3D — equivalentes aproximados a Tailwind `*-300`
const STOP_COLORS_HEX = [
  '#7dd3fc', // sky
  '#fcd34d', // amber
  '#6ee7b7', // emerald
  '#fda4af', // rose
  '#c4b5fd', // violet
  '#bef264', // lime
  '#fdba74', // orange
  '#67e8f9', // cyan
];

const colorForIndex = (i: number) => STOP_COLORS[i % STOP_COLORS.length];
const hexForIndex = (i: number) => STOP_COLORS_HEX[i % STOP_COLORS_HEX.length];

// Dimensiones de un semirremolque estándar EU (cm). 1360 × 240 → caben 26
// palets de 120×100 (2 columnas × 13 filas) o 33 de 120×80 con zigzag.
const TRUCK_LENGTH_CM = 1360;
const TRUCK_WIDTH_CM = 240;
const TRUCK_HEIGHT_CM = 265;
const PALLET_HEIGHT_CM = 180;
// Fallback si la ruta no tiene PalletType asociado.
const DEFAULT_PALLET_LENGTH_CM = 120;
const DEFAULT_PALLET_WIDTH_CM = 100;

/**
 * Orienta el palet dentro del camión: el lado más largo va a lo ancho del
 * camión si caben 2 (maximiza palets por fila). Devuelve el footprint
 * efectivo (X = profundidad cabina→puerta, Y = ancho).
 */
const palletFootprint = (lengthCm: number, widthCm: number): { palletX: number; palletY: number } => {
  const longSide = Math.max(lengthCm, widthCm);
  const shortSide = Math.min(lengthCm, widthCm);
  if (longSide * 2 <= TRUCK_WIDTH_CM) {
    // Lado largo a lo ancho → caben 2 por fila, profundidad mínima
    return { palletX: shortSide, palletY: longSide };
  }
  // No caben 2 lados largos en paralelo → lado corto a lo ancho
  return { palletX: longSide, palletY: shortSide };
};

/** Trunca y añade elipsis si el texto supera maxChars. No abrevia nada más. */
const truncate = (s: string, maxChars: number): string =>
  s.length <= maxChars ? s : s.slice(0, maxChars - 1) + '…';

const stopAddressLine = (stop: TruckLoadStop) =>
  [stop.addressLine, [stop.postalCode, stop.city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

interface TruckScenePallet {
  stopIndex: number;
  stop: TruckLoadStop;
  customerName: string;
  palletNumber: number;
  palletTotal: number;
  loadIndex: number; // 0-based posición en orden de carga
}


/**
 * Vista cenital 2D del remolque en SVG. Cabina a la izquierda, puerta a la
 * derecha. Los palets NO se apilan: cada uno ocupa una casilla del suelo.
 * Dos columnas paralelas, se rellenan desde cabina hacia la puerta siguiendo
 * el orden LIFO de carga (primer palet en entrar = junto a la cabina).
 */
interface TruckTopViewProps {
  pallets: TruckScenePallet[];
  /** Dimensiones del tipo de palet más usado en la ruta (cm). Si null, fallback 120×100. */
  palletLengthCm: number | null;
  palletWidthCm: number | null;
}

const TruckTopView: React.FC<TruckTopViewProps> = ({ pallets, palletLengthCm, palletWidthCm }) => {
  // Trabajamos directamente en cm dentro del viewBox. El SVG escala solo.
  const W = TRUCK_LENGTH_CM;
  const H = TRUCK_WIDTH_CM;
  const PAD = 80;
  const { palletX, palletY } = palletFootprint(
    palletLengthCm ?? DEFAULT_PALLET_LENGTH_CM,
    palletWidthCm ?? DEFAULT_PALLET_WIDTH_CM,
  );
  // Cuántos palets caben en paralelo (1 ó 2 normalmente)
  const palletsPerRow = Math.max(1, Math.floor(H / palletY));
  // Centramos en Y si sobra espacio (ej. 240/100 = 2 → encajado; con 240/120 = 2 también)
  const yOffset = (H - palletsPerRow * palletY) / 2;

  return (
    <svg
      viewBox={`${-PAD} ${-PAD} ${W + PAD * 2} ${H + PAD * 2}`}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Suelo del remolque (tablero de madera) */}
      <rect x={0} y={0} width={W} height={H} fill="#fef3c7" stroke="#78350f" strokeWidth={4} rx={6} />

      {/* Rejilla de divisiones de palet: cada línea cae donde acabaría un palet,
          así se ve a simple vista qué casillas quedan libres. */}
      {Array.from({ length: Math.max(0, Math.floor(W / palletX) - 1) }, (_, i) => (i + 1) * palletX)
        .filter(x => x < W)
        .map(x => (
          <line
            key={`vsplit-${x}`}
            x1={x}
            y1={2}
            x2={x}
            y2={H - 2}
            stroke="#78350f"
            strokeWidth={0.6}
            strokeDasharray="3 3"
            opacity={0.55}
          />
        ))}
      {Array.from({ length: Math.max(0, palletsPerRow - 1) }, (_, i) => yOffset + (i + 1) * palletY)
        .map(y => (
          <line
            key={`hsplit-${y}`}
            x1={2}
            y1={y}
            x2={W - 2}
            y2={y}
            stroke="#78350f"
            strokeWidth={0.6}
            strokeDasharray="3 3"
            opacity={0.55}
          />
        ))}

      {/* Pared cabina (izquierda) */}
      <rect x={-18} y={-4} width={18} height={H + 8} fill="#1e293b" rx={3} />
      <text
        x={-46}
        y={H / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={26}
        fontWeight={700}
        fill="#0f172a"
        transform={`rotate(-90 -46 ${H / 2})`}
        style={{ letterSpacing: 2 }}
      >
        CABINA
      </text>

      {/* Puerta trasera (derecha): dos hojas abiertas */}
      <rect x={W} y={-4} width={6} height={H / 2 + 4} fill="#dc2626" rx={2} />
      <rect x={W} y={H / 2} width={6} height={H / 2 + 4} fill="#dc2626" rx={2} />
      <text
        x={W + 46}
        y={H / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={26}
        fontWeight={700}
        fill="#dc2626"
        transform={`rotate(90 ${W + 46} ${H / 2})`}
        style={{ letterSpacing: 2 }}
      >
        PUERTA
      </text>

      {/* Flecha sentido de carga (sutil, en la cabecera) */}
      <line
        x1={20}
        y1={-PAD / 2}
        x2={W - 20}
        y2={-PAD / 2}
        stroke="#94a3b8"
        strokeWidth={2}
        strokeDasharray="6 4"
        markerEnd="url(#arrow)"
      />
      <defs>
        <marker
          id="arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" fill="#94a3b8" />
        </marker>
      </defs>
      <text x={W / 2} y={-PAD / 2 - 8} textAnchor="middle" fontSize={14} fill="#64748b">
        sentido de carga
      </text>

      {/* Palets — el lado más largo va a lo ancho del camión para maximizar densidad. */}
      {pallets.map((p, i) => {
        const row = Math.floor(i / palletsPerRow);
        const col = i % palletsPerRow;
        // Sin gap entre palets: van pegados unos a otros, como en el camión real.
        const x = row * palletX;
        const y = yOffset + col * palletY;
        const cx = x + palletX / 2;
        const color = hexForIndex(p.stopIndex);
        const title = `${p.customerName} · #${p.loadIndex + 1} carga · Parada ${p.stopIndex + 1} · ${p.stop.addressName || stopAddressLine(p.stop)} · palet ${p.palletNumber}/${p.palletTotal}`;

        // Truncado simple, sin abreviaciones. Estimación basada en el ancho disponible.
        // ~6.5 cm por carácter a font-size 11 → palletX/6.5 chars caben.
        const nameMaxChars = Math.max(6, Math.floor(palletX / 6.5));
        const displayName = truncate(p.customerName, nameMaxChars);

        return (
          <g key={`${p.stop.stopKey}-${p.customerName}-${p.palletNumber}`}>
            <title>{title}</title>
            <rect
              x={x + 1}
              y={y + 1}
              width={palletX - 2}
              height={palletY - 2}
              fill={color}
              stroke="#0f172a"
              strokeWidth={1.5}
              rx={3}
            />

            {/* 1) Nombre del cliente (truncado) — pista visual rápida */}
            <text x={cx} y={y + 18} textAnchor="middle" fontSize={11} fontWeight={800} fill="#0f172a">
              {displayName}
            </text>

            {/* Separador */}
            <line
              x1={x + 6}
              y1={y + 24}
              x2={x + palletX - 6}
              y2={y + 24}
              stroke="#0f172a"
              strokeWidth={0.5}
              opacity={0.5}
            />

            {/* 2) #carga grande, ocupa el centro vertical */}
            <text x={cx} y={y + 50} textAnchor="middle" fontSize={22} fontWeight={900} fill="#0f172a">
              #{p.loadIndex + 1}
            </text>

            {/* 3) Parada y PALET en líneas separadas — hay espacio vertical */}
            <text x={cx} y={y + 70} textAnchor="middle" fontSize={10} fontWeight={700} fill="#0f172a">
              Parada {p.stopIndex + 1}
            </text>
            <text x={cx} y={y + 86} textAnchor="middle" fontSize={10} fontWeight={700} fill="#0f172a">
              P{p.palletNumber}/{p.palletTotal}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

interface SortableStopProps {
  stop: TruckLoadStop;
  order: number; // 1-based posición en el orden de entrega
  color: string;
}

const SortableStopCard: React.FC<SortableStopProps> = ({ stop, order, color }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.stopKey,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3 shadow-sm"
    >
      <button
        type="button"
        className="touch-none cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600"
        {...attributes}
        {...listeners}
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical size={18} />
      </button>
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs border ${color}`}>
        {order}
      </div>
      <div className="flex-1 min-w-0">
        {stop.addressName && (
          <p className="text-sm font-semibold text-slate-900 truncate">{stop.addressName}</p>
        )}
        <p className="text-xs text-slate-700 truncate flex items-center gap-1">
          <MapPin size={11} className="text-slate-400 shrink-0" />
          {stopAddressLine(stop) || 'Dirección sin detalle'}
        </p>
        <p className="text-xs text-slate-500 truncate mt-0.5">
          {stop.orders.map(o => o.customerName).join(' · ')}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-xs text-slate-500">Palets</p>
        <p className="text-lg font-bold text-slate-900 leading-none">{stop.totalPallets}</p>
      </div>
    </div>
  );
};

export const TruckLoadDialog: React.FC<TruckLoadDialogProps> = ({
  isOpen,
  onClose,
  routeId,
  routeName,
  date,
}) => {
  const [data, setData] = useState<TruckLoadView | null>(null);
  const [stops, setStops] = useState<TruckLoadStop[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [optimizeInfo, setOptimizeInfo] = useState<{ distanceKm: number; durationMin: number } | null>(null);
  // Procedencia del orden visible. Se inicializa con lo que devuelve el back y
  // se actualiza al optimizar / arrastrar para mantener el badge en sincronía.
  const [orderingSource, setOrderingSource] = useState<'optimized' | 'manual' | null>(null);
  const [orderingUpdatedAt, setOrderingUpdatedAt] = useState<string | null>(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printOkMsg, setPrintOkMsg] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    ordersApi
      .getTruckLoad(routeId, date)
      .then(res => {
        if (cancelled) return;
        setData(res);
        setStops(res.stops);
        setOrderingSource(res.orderingSource);
        setOrderingUpdatedAt(res.orderingUpdatedAt);
        // Las métricas se cargan independientemente del origen (optimized o manual):
        // el backend ahora persiste ambas y queremos mostrarlas igual al reabrir.
        if (res.orderingDistanceKm != null && res.orderingDurationMin != null) {
          setOptimizeInfo({ distanceKm: res.orderingDistanceKm, durationMin: res.orderingDurationMin });
        } else {
          setOptimizeInfo(null);
        }
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.message || 'No se pudo cargar la vista de carga');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, routeId, date]);

  // Paradas con coordenadas → candidatas a entrar en el cálculo de OSRM.
  const stopsWithCoords = useMemo(
    () => stops.filter(s => s.latitude != null && s.longitude != null),
    [stops],
  );
  const stopsWithoutCoords = stops.length - stopsWithCoords.length;

  const persistOrdering = async (
    nextStops: TruckLoadStop[],
    source: 'optimized' | 'manual',
    metrics?: { distanceKm: number; durationMin: number },
  ) => {
    setIsSavingOrder(true);
    try {
      await ordersApi.setTruckLoadOrdering(routeId, date, {
        stopKeys: nextStops.map(s => s.stopKey),
        source,
        distanceKm: metrics?.distanceKm ?? null,
        durationMin: metrics?.durationMin ?? null,
      });
      setOrderingSource(source);
      setOrderingUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setOptimizeError(e?.message || 'No se pudo guardar el orden.');
    } finally {
      setIsSavingOrder(false);
    }
  };

  /**
   * Tras cualquier cambio manual del orden, recalculamos las métricas con OSRM
   * (perfil driving, orden fijo) para que el usuario vea distancia/tiempo de su
   * ruta. Si falla o falta alguna coordenada, persistimos el orden sin métricas.
   */
  const recomputeMetricsAndPersist = async (next: TruckLoadStop[]) => {
    const withCoords = next.filter(s => s.latitude != null && s.longitude != null);
    if (withCoords.length < 2) {
      // No hay suficientes puntos con coords; guardamos solo el orden.
      await persistOrdering(next, 'manual');
      setOptimizeInfo(null);
      return;
    }
    try {
      const points = [
        { key: '__origin__', latitude: PUENTE_GENIL.latitude, longitude: PUENTE_GENIL.longitude },
        ...withCoords.map(s => ({
          key: s.stopKey,
          latitude: s.latitude as number,
          longitude: s.longitude as number,
        })),
        // Cerramos el ciclo volviendo a Puente Genil para que la métrica incluya el regreso.
        { key: '__origin_back__', latitude: PUENTE_GENIL.latitude, longitude: PUENTE_GENIL.longitude },
      ];
      const metrics = await computeRouteDistance(points);
      setOptimizeInfo(metrics);
      await persistOrdering(next, 'manual', metrics);
    } catch (e: any) {
      // Si OSRM falla, mantenemos el orden manual pero sin métricas.
      setOptimizeInfo(null);
      await persistOrdering(next, 'manual');
    }
  };

  const handlePreviewPrint = async () => {
    setPrintError(null);
    setPrintOkMsg(null);
    try {
      setIsPreviewing(true);
      await ordersApi.previewTruckLoad(routeId, date);
    } catch (e: any) {
      setPrintError(e?.message || 'No se pudo abrir la vista previa.');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handlePrint = async () => {
    setPrintError(null);
    setPrintOkMsg(null);
    try {
      setIsPrinting(true);
      await ordersApi.printTruckLoad(routeId, date);
      setPrintOkMsg('Encolado en la impresora de vista de carga.');
    } catch (e: any) {
      setPrintError(e?.message || 'No se pudo imprimir la vista de carga.');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleOptimize = async () => {
    if (stopsWithCoords.length < 2) {
      setOptimizeError('Necesitas al menos 2 paradas con coordenadas para calcular la ruta.');
      return;
    }
    setIsOptimizing(true);
    setOptimizeError(null);
    setOptimizeInfo(null);
    try {
      // Puente Genil va como primera coordenada y se fija como origen. Las
      // paradas sin coordenadas se respetan en su posición actual (no entran
      // al cálculo) — quedan al final tras las optimizadas.
      const points = [
        { key: '__origin__', latitude: PUENTE_GENIL.latitude, longitude: PUENTE_GENIL.longitude },
        ...stopsWithCoords.map(s => ({
          key: s.stopKey,
          latitude: s.latitude as number,
          longitude: s.longitude as number,
        })),
      ];
      const result = await optimizeRoute(points, { roundtrip: true, fixedStart: true });

      // result.orderedKeys empieza por '__origin__' (Puente Genil). Quitamos esa
      // entrada y aplicamos el orden a las paradas con coords.
      const ordered = result.orderedKeys.filter(k => k !== '__origin__');
      const byKey = new Map(stops.map(s => [s.stopKey, s]));
      const optimized = ordered.map(k => byKey.get(k)).filter(Boolean) as TruckLoadStop[];
      const leftovers = stops.filter(s => s.latitude == null || s.longitude == null);
      const next = [...optimized, ...leftovers];
      setStops(next);
      setOptimizeInfo({ distanceKm: result.distanceKm, durationMin: result.durationMin });
      await persistOrdering(next, 'optimized', { distanceKm: result.distanceKm, durationMin: result.durationMin });
    } catch (e: any) {
      setOptimizeError(e?.message || 'No se pudo calcular la ruta óptima.');
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = stops.findIndex(s => s.stopKey === active.id);
    const newIndex = stops.findIndex(s => s.stopKey === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(stops, oldIndex, newIndex);
    setStops(next);
    // Edición manual: pedimos a OSRM las métricas del nuevo orden y persistimos.
    void recomputeMetricsAndPersist(next);
  };

  // Orden de carga LIFO: la última parada en entregar entra primero al camión
  // (queda al fondo, junto a la cabina); la primera parada en entregar entra
  // última (queda junto a la puerta trasera para salir primero). Iteramos por
  // comandas dentro de la parada para que cada palet sepa a qué cliente
  // pertenece y mostremos su nombre — eso es lo que el operario reconoce.
  const loadOrder = useMemo(() => {
    const out: {
      stopIndex: number;
      stop: TruckLoadStop;
      customerName: string;
      palletNumber: number;
      palletTotal: number;
    }[] = [];
    for (let i = stops.length - 1; i >= 0; i--) {
      const stop = stops[i];
      for (const order of stop.orders) {
        for (let p = 1; p <= order.palletCount; p++) {
          out.push({
            stopIndex: i,
            stop,
            customerName: order.customerName,
            palletNumber: p,
            palletTotal: order.palletCount,
          });
        }
      }
    }
    return out;
  }, [stops]);

  const totalPallets = stops.reduce((s, st) => s + st.totalPallets, 0);

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-[95vw] md:!max-w-[90vw] p-0 overflow-hidden gap-0 max-h-[90vh] flex flex-col">
        <DialogHeader className="p-5 border-b border-slate-100 bg-slate-50/50 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Truck size={22} className="text-slate-600" />
                Vista de carga · {routeName}
              </DialogTitle>
              <p className="text-sm text-slate-500 mt-1">
                Punto de partida: Puente Genil · Fecha: {date}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0 mr-8">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePreviewPrint}
                disabled={isPreviewing || !data || stops.length === 0}
                className="gap-1.5"
                title="Abrir la hoja imprimible en una pestaña nueva, sin enviarla a la impresora"
              >
                <Eye size={14} />
                {isPreviewing ? 'Abriendo…' : 'Vista previa'}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handlePrint}
                disabled={isPrinting || !data || stops.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                title="Encolar la hoja en la impresora de vista de carga (configurada en Ajustes)"
              >
                <Printer size={14} />
                {isPrinting ? 'Imprimiendo…' : 'Imprimir'}
              </Button>
            </div>
          </div>
          {(printError || printOkMsg) && (
            <div className="mt-2 text-xs">
              {printError && <span className="text-red-700">{printError}</span>}
              {printOkMsg && !printError && <span className="text-green-700">{printOkMsg}</span>}
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-red-700 bg-red-50 border border-red-100 m-5 rounded-lg">
              {error}
            </div>
          ) : !data || (stops.length === 0 && data.ordersWithoutAddress.length === 0) ? (
            <div className="p-10 text-center text-slate-500">
              No hay comandas en esta ruta para la fecha seleccionada.
            </div>
          ) : (
            <div className="p-5 grid grid-cols-1 lg:grid-cols-[34%_66%] gap-6">
              {/* Columna izquierda: orden de entrega editable */}
              <div className="space-y-3">
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <MapPin size={16} className="text-slate-500" />
                      Orden de entrega
                    </h3>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleOptimize}
                      disabled={isOptimizing || stopsWithCoords.length < 2}
                      className="h-7 text-[11px] gap-1"
                      title="Calcular el orden óptimo de visita con OSRM (OpenStreetMap)"
                    >
                      {isOptimizing ? 'Calculando…' : 'Calcular ruta óptima'}
                    </Button>
                  </div>

                  {/* Badge con la procedencia del orden actual */}
                  <div className="mt-1.5 flex items-center gap-2 text-[11px]">
                    {orderingSource === 'optimized' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-300">
                        🛣️ Orden óptimo (OSRM)
                      </span>
                    )}
                    {orderingSource === 'manual' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-300">
                        ✋ Editado a mano
                      </span>
                    )}
                    {orderingSource === null && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600 border border-slate-200">
                        Sin orden guardado (alfabético por CP)
                      </span>
                    )}
                    {orderingUpdatedAt && (
                      <span className="text-slate-400">
                        · {new Date(orderingUpdatedAt).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    )}
                    {isSavingOrder && <span className="text-slate-400">· guardando…</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Arrastra las paradas para fijar el orden en que las visitará el conductor —
                    la 1 es la primera entrega, la última es la última entrega. Si pulsas
                    "Calcular ruta óptima", el sistema usa OpenStreetMap para reordenarlas
                    minimizando tiempo desde Puente Genil.
                  </p>
                  {stopsWithoutCoords > 0 && (
                    <p className="text-[11px] text-amber-700 mt-1">
                      {stopsWithoutCoords} parada{stopsWithoutCoords === 1 ? '' : 's'} sin coordenadas — no entran en el cálculo. Edita esas direcciones para que se geocodifiquen.
                    </p>
                  )}
                  {optimizeInfo && (
                    <p className="text-[11px] text-green-700 mt-1">
                      {orderingSource === 'manual' ? 'Tu ruta' : 'Ruta calculada'}: {optimizeInfo.distanceKm.toFixed(1)} km · ≈ {Math.round(optimizeInfo.durationMin)} min (estimación OSRM, ida y vuelta a Puente Genil).
                    </p>
                  )}
                  {optimizeError && (
                    <p className="text-[11px] text-red-700 mt-1">{optimizeError}</p>
                  )}
                </div>

                {stops.length > 0 && (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={stops.map(s => s.stopKey)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {stops.map((stop, i) => (
                          <SortableStopCard
                            key={stop.stopKey}
                            stop={stop}
                            order={i + 1}
                            color={colorForIndex(i)}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                {data.ordersWithoutAddress.length > 0 && (
                  <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-3">
                    <h4 className="text-xs font-semibold text-amber-900 flex items-center gap-1.5 mb-2">
                      <AlertTriangle size={14} />
                      Comandas sin dirección ({data.ordersWithoutAddress.length})
                    </h4>
                    <p className="text-xs text-amber-800 mb-2">
                      Estas comandas no entran en el orden de carga. Asígnales una dirección
                      de entrega para incluirlas.
                    </p>
                    <ul className="text-xs text-amber-900 space-y-1">
                      {data.ordersWithoutAddress.map(o => (
                        <li key={o.orderId} className="flex justify-between">
                          <span className="truncate">{o.customerName}</span>
                          <span className="ml-2 font-mono">{o.palletCount} palet(s)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Columna derecha: visualización del camión */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    <Truck size={16} className="text-slate-500" />
                    Orden de carga ({totalPallets} palet{totalPallets === 1 ? '' : 's'})
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Según el orden de entrega que has definido a la izquierda: la última parada (la de abajo de la lista) entra primera al camión y queda pegada a la cabina; la primera parada (la de arriba) entra última y queda junto a la puerta para salir antes.
                  </p>
                </div>

                {/* Camión — planta cenital 2D */}
                <div className="border border-slate-300 rounded-lg overflow-hidden bg-slate-50">
                  <div className="bg-white p-3" style={{ minHeight: 320 }}>
                    {loadOrder.length === 0 ? (
                      <div className="h-72 flex items-center justify-center text-xs text-slate-400 italic">
                        Sin palets para cargar
                      </div>
                    ) : (
                      <TruckTopView
                        pallets={loadOrder.map((p, idx) => ({
                          stopIndex: p.stopIndex,
                          stop: p.stop,
                          customerName: p.customerName,
                          palletNumber: p.palletNumber,
                          palletTotal: p.palletTotal,
                          loadIndex: idx,
                        }))}
                        palletLengthCm={data?.palletLengthCm ?? null}
                        palletWidthCm={data?.palletWidthCm ?? null}
                      />
                    )}
                  </div>
                  <div className="bg-slate-100 px-2 py-1 text-[10px] text-slate-500 flex items-center justify-between border-t border-slate-200">
                    <span>Cabina · primero en cargar</span>
                    <ArrowRight size={12} className="text-slate-400" />
                    <span>Puerta · primero en salir</span>
                  </div>
                </div>

                {/* Lista textual del orden */}
                {loadOrder.length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                      <Package size={13} className="text-slate-500" />
                      Lista numerada
                    </div>
                    <ol className="divide-y divide-slate-100 text-xs">
                      {loadOrder.map((p, idx) => (
                        <li key={`row-${p.stop.stopKey}-${p.customerName}-${p.palletNumber}`} className="px-3 py-2 flex items-center gap-3">
                          <span className="font-mono font-bold text-slate-700 w-6 text-right">{idx + 1}.</span>
                          <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] border ${colorForIndex(p.stopIndex)}`}>
                            {p.stopIndex + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 truncate">
                              {p.customerName} · palet {p.palletNumber}/{p.palletTotal}
                            </p>
                            <p className="text-slate-500 truncate text-[11px]">
                              {p.stop.addressName || stopAddressLine(p.stop)}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50 shrink-0">
          <Button variant="outline" onClick={onClose} className="px-6">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
